import { v4 } from 'uuid';
import {
  DuckDbTable,
  fromArrowRow,
  parseRowCount,
  type DuckDbWriteTarget,
} from '@cotera/client/app/etc/duckdb';

/**
 * How many ids one read pulls out of the snapshot.
 *
 * Deliberately much larger than the batch a caller asks for. Dispatch batches
 * are sized by what one request should carry — tens of rows — and querying once
 * per batch would put a DuckDB round trip in front of every one of them. Reading
 * a page of this size and handing it out in slices costs one query per few
 * hundred batches instead.
 */
const ROW_ID_PAGE_SIZE = 5_000;

/**
 * A row-id set that is read a page at a time rather than held whole.
 *
 * The point is what it does *not* do: a bulk run over a large dataset used to
 * materialise every id into a JS array and keep it there for the duration of a
 * dispatch loop that can run for a long time. The ids live in DuckDB — where the
 * dataset already is — and only one page is ever in the JS heap.
 */
export type DatasetRowIdCursor = {
  /** Rows in the snapshot. Exact, because the snapshot has already been taken. */
  readonly total: number;
  /**
   * Yields ids `size` at a time, in a stable order, each id exactly once.
   *
   * The final batch is short rather than padded. Iterating twice re-reads the
   * snapshot from the start, which is well-defined but not something any caller
   * needs today.
   */
  batches: (size: number) => AsyncGenerator<readonly string[]>;
  /** Drops the snapshot. Idempotent, so a `finally` can always call it. */
  close: () => Promise<void>;
};

/** A cursor over nothing, for a dataset with no durable row id. */
export const emptyDatasetRowIdCursor = (): DatasetRowIdCursor => ({
  total: 0,
  async *batches() {
    yield* [];
  },
  async close() {
    return;
  },
});

/**
 * Snapshots the ids `selectSql` yields, and hands back a cursor over them.
 *
 * Snapshotted rather than re-queried per page, and that is the substance of this
 * module. A caller's predicate can read columns that change *while it iterates*
 * — the bulk-run menu's "needs run" filter reads the per-workflow state columns,
 * which the automations overlay repaints as runs land — so re-running it for
 * page two would silently skip every row that moved in the meantime. Taking the
 * set once means a page boundary cannot lose a row.
 *
 * `row_number()` supplies the paging key. Ordering by the id itself would work
 * but would sort the whole set on every page; a monotonic integer makes each page
 * a range scan, and it is dense, so no row can fall between two pages.
 *
 * The table is named per cursor rather than reused: two open cursors are rare but
 * legal, and a shared name would have the second `CREATE OR REPLACE` pull the
 * rows out from under the first one's iteration.
 */
export const openDatasetRowIdCursor = async (params: {
  db: DuckDbWriteTarget;
  /** A SELECT yielding exactly one `row_id` column. */
  selectSql: string;
  /**
   * Ids read per query. Defaults to {@link ROW_ID_PAGE_SIZE}; only specs set it,
   * because paging over a realistic page size would need a fixture larger than
   * the behaviour being proved.
   */
  pageSize?: number;
}): Promise<DatasetRowIdCursor> => {
  const table = new DuckDbTable({
    name: `dataset_run_row_ids_${v4().replaceAll('-', '')}`,
  });
  await table.replace(params.db, {
    from: 'query',
    sql: `SELECT row_id, row_number() OVER () AS ord FROM (${params.selectSql})`,
  });

  const counted = await params.db.rawQuery(
    `SELECT count(*) AS row_count FROM ${table.sql}`
  );
  const total =
    parseRowCount(fromArrowRow(counted.toArray()[0] ?? {})['row_count']) ?? 0;

  let closed = false;

  const close = async (): Promise<void> => {
    if (closed) {
      return;
    }
    // Set before the await, so two concurrent closes cannot both issue the DROP.
    closed = true;
    await params.db.rawQuery(`DROP TABLE IF EXISTS ${table.sql}`);
  };

  async function* batches(size: number): AsyncGenerator<readonly string[]> {
    if (closed || total === 0) {
      return;
    }
    // Never smaller than the batch asked for: a page that could not fill one
    // batch would put a query in front of every batch, which is what the coarse
    // default exists to avoid.
    const pageSize = Math.max(size, params.pageSize ?? ROW_ID_PAGE_SIZE);
    let after = 0;
    let pending: string[] = [];

    while (!closed) {
      const page = await params.db.rawQuery(
        `SELECT row_id, ord FROM ${table.sql} WHERE ord > ${after} ORDER BY ord LIMIT ${pageSize}`
      );
      const rows = page.toArray().map((row) => fromArrowRow(row));
      if (rows.length === 0) {
        break;
      }
      for (const row of rows) {
        const rowId = row['row_id'];
        if (typeof rowId === 'string') {
          pending.push(rowId);
        }
      }
      // From the last row rather than by adding `pageSize`: a page shorter than
      // asked for still has to leave the cursor where the rows actually stopped.
      const last = rows[rows.length - 1]?.['ord'];
      if (typeof last !== 'number') {
        break;
      }
      after = last;

      while (pending.length >= size) {
        yield pending.slice(0, size);
        pending = pending.slice(size);
      }
      if (rows.length < pageSize) {
        break;
      }
    }

    // Whatever the last page could not fill a batch with. Not emitted inside the
    // loop, because a short batch mid-iteration would mean a caller sizing its
    // requests by `size` sent needlessly small ones.
    if (!closed && pending.length > 0) {
      yield pending;
    }
  }

  return { total, batches, close };
};
